import React, { useContext, useEffect, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../../components/layout/layout';
import { AppContext } from '../../context/appContext';
import { GET_USER_ROLES } from '../../components/helper/constant';
import TenantTable from '../../components/superAdmin/tenantTable';
import TenantFormModal from '../../components/superAdmin/tenantFormModal';
import {
  createTenant,
  listTenants,
  setTenantStatus,
  updateTenant,
} from '../../services/api/superAdminTenantApi';
import {
  extractTenantError,
  getTenantErrorMessage,
  shouldForceLogoutOnTenantError,
} from '../../utils/tenantErrors';

const TenantsPage = () => {
  const navigate = useNavigate();
  const { profile, getRole, logOut } = useContext(AppContext);

  const [tenants, setTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const isSuperAdmin = getRole() === GET_USER_ROLES.SUPER_ADMIN;

  const handleTenantError = (error, fallbackMessage) => {
    const normalized = extractTenantError(error);
    const message = getTenantErrorMessage(normalized.code, fallbackMessage);

    toast.error(message);

    if (shouldForceLogoutOnTenantError(normalized.code)) {
      logOut();
      navigate('/');
    }
  };

  const loadTenants = async () => {
    setIsLoading(true);
    try {
      const result = await listTenants();
      setTenants(result);
    } catch (error) {
      handleTenantError(error, 'Failed to load tenants.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!profile) return;

    if (!isSuperAdmin) {
      navigate('/');
      return;
    }

    loadTenants();
  }, [profile, isSuperAdmin]);

  const openCreateModal = () => {
    setSelectedTenant(null);
    setShowFormModal(true);
  };

  const openEditModal = (tenant) => {
    setSelectedTenant(tenant);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (isSubmitting) return;
    setShowFormModal(false);
    setSelectedTenant(null);
  };

  const handleSubmitTenant = async (values) => {
    setIsSubmitting(true);

    try {
      if (selectedTenant) {
        await updateTenant(selectedTenant.id, values);
        toast.success('Tenant updated successfully.');
      } else {
        await createTenant(values);
        toast.success('Tenant created successfully.');
      }

      closeFormModal();
      await loadTenants();
    } catch (error) {
      handleTenantError(error, 'Unable to save tenant.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusChange = async (tenant, status) => {
    setIsSubmitting(true);
    try {
      await setTenantStatus(tenant.id, status);
      toast.success(`Tenant status updated to ${status}.`);
      await loadTenants();
    } catch (error) {
      handleTenantError(error, 'Unable to update tenant status.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <Layout>
      <main className="flex-grow-1 d-flex dashboard">
        <Container fluid className="d-flex flex-column p-0">
          <div className="pageHead d-flex justify-content-between align-items-center">
            <h1 className="title mb-0">Tenant Management</h1>
            <Button onClick={openCreateModal} aria-label="add-tenant">
              Add Tenant
            </Button>
          </div>

          <TenantTable
            tenants={tenants}
            isLoading={isLoading}
            onEdit={openEditModal}
            onChangeStatus={handleStatusChange}
            onRefresh={loadTenants}
          />
        </Container>
      </main>

      <TenantFormModal
        show={showFormModal}
        mode={selectedTenant ? 'edit' : 'create'}
        tenant={selectedTenant}
        isSubmitting={isSubmitting}
        onClose={closeFormModal}
        onSubmit={handleSubmitTenant}
      />
    </Layout>
  );
};

export default TenantsPage;
